import toastTrigger from '@ui/toast/toast-trigger';
import { EToastTypes } from '@ui/toast/toast.types';
import { openDB, DBSchema, IDBPDatabase } from 'idb';

interface MiscDB extends DBSchema {
  misc: {
    value: StoreItem<unknown>;
    key: string;
    indexes: {
      uuid: string;
      type: string;
      category: string;
    };
  };
}

export type StoreItemInput<T> = {
  uuid: string;
  type: string;
  data: T;
  category?: string;
  expdate?: number;
};

export type StoreItem<T> = {
  uuid: string;
  type: string;
  data: T;
  key: string;
  timestamp: number;
  category?: string;
  expdate?: number;
};

class Db {
  private db: IDBPDatabase<MiscDB> | undefined;
  private retryCount = 0;

  async open() {
    try {
      this.db = await openDB<MiscDB>('misc', 1, {
        upgrade(db) {
          const productStore = db.createObjectStore('misc', {
            keyPath: 'key',
          });
          productStore.createIndex('uuid', 'uuid');
          productStore.createIndex('type', 'type');
          productStore.createIndex('category', 'category');
        },
      });
    } catch (error) {
      console.error('Error opening indexedDB', error);
      if (this.retryCount < 5) {
        this.retryCount++;
        setTimeout(() => this.open(), 500);
      } else {
        toastTrigger('There was an error opening browser storage', {
          description: 'Please make sure you are not in private mode or try using different browser',
          type: EToastTypes.error,
          autoClose: false,
        });
      }
    }
  }

  /**
   * Add a value to a store.
   * Rejects if an item of a given key already exists in the store.
   *
   * @param data
   * @param ttl seconds, minutes, hours, or days
   *
   **/
  async add<T>(data: StoreItemInput<T>, ttl?: Ttl) {
    if (!this.db) return;
    // Add an article:
    await this.db.add('misc', {
      ...data,
      key: `${data.type}-${data.uuid}`,
      timestamp: Date.now(),
      expdate: getTimestampFromTtl(ttl),
    });
  }

  async addMulti<T>(data: StoreItemInput<T>[]) {
    if (!this.db) return;
    // Add an article:
    const tx = this.db.transaction('misc', 'readwrite');
    await Promise.all(
      data.map((item) => tx.store.add({ ...item, key: `${item.type}-${item.uuid}`, timestamp: Date.now() }))
    );
  }

  /**
   * Put an item in the database.
   * Replaces any item with the same key.
   */
  async put<T>(data: StoreItemInput<T>, ttl?: Ttl) {
    if (!this.db) return;
    // Add an article:
    await this.db.put('misc', {
      ...data,
      key: `${data.type}-${data.uuid}`,
      timestamp: Date.now(),
      expdate: getTimestampFromTtl(ttl),
    });
  }

  /**
   * Put multiple items in the database.
   * Replaces any item with the same key.
   */
  async putMulti<T>(data: StoreItemInput<T>[]) {
    if (!this.db) return;
    // Add an article:
    const tx = this.db.transaction('misc', 'readwrite');
    await Promise.all(
      data.map((item) => tx.store.put({ ...item, key: `${item.type}-${item.uuid}`, timestamp: Date.now() }))
    );
  }

  async get<T>(type: string, uuid: string): Promise<StoreItem<T> | null> {
    if (!this.db) return null;
    const result = (await this.db.get('misc', `${type}-${uuid}`)) as StoreItem<T> | undefined;
    if (result?.expdate && result?.expdate < Date.now()) {
      this.delete(type, uuid);
      return null;
    }
    return result || null;
  }

  async getAll() {
    if (!this.db) return;
    const results = await this.db.getAll('misc');
    const now = Date.now();
    return results.filter((result) => {
      if (result?.expdate && result?.expdate < now) {
        this.delete(result.type, result.uuid);
        return false;
      }
      return true;
    });
  }

  async delete(type: string, uuid: string) {
    if (!this.db) return;
    await this.db.delete('misc', `${type}-${uuid}`);
  }

  async clear() {
    if (!this.db) return;
    await this.db.clear('misc');
  }

  async getFromIndex(index: 'uuid' | 'type' | 'category', query?: string, count?: number) {
    if (!this.db) return;
    return await this.db.getAllFromIndex('misc', index, query, count);
  }
}

const db = new Db();
db.open();

export default db;

type TtlSeconds = {
  seconds: number;
  minutes?: never;
  hours?: never;
  days?: never;
};

type TtlMinutes = {
  seconds?: never;
  minutes: number;
  hours?: never;
  days?: never;
};

type TtlHours = {
  seconds?: never;
  minutes?: never;
  hours: number;
  days?: never;
};

type TtlDays = {
  seconds?: never;
  minutes?: never;
  hours?: never;
  days: number;
};

export type Ttl = TtlSeconds | TtlMinutes | TtlHours | TtlDays;

const getTimestampFromTtl = (ttl?: Ttl): number | undefined => {
  if (!ttl) return undefined;

  if (ttl.seconds) {
    return Date.now() + ttl.seconds * 1000;
  }

  if (ttl.minutes) {
    return Date.now() + ttl.minutes * 1000 * 60;
  }

  if (ttl.hours) {
    return Date.now() + ttl.hours * 1000 * 60 * 60;
  }

  if (ttl.days) {
    return Date.now() + ttl.days * 1000 * 60 * 60 * 24;
  }
};
